import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const BASE = 'https://menu.searchisend.in';

const ManageServiceRequests = ({ token, user }) => {
    const [requests, setRequests] = useState([]);
    const [kitchenAlerts, setKitchenAlerts] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchRequests = useCallback(async () => {
        if (!user?.id || !token) return;
        try {
            // Fetch Customer Requests
            const res = await fetch(`${BASE}/apimenu/get_service_requests.php?customer_id=${user.id}`);
            const data = await res.json();
            if (data.status === 'success') {
                setRequests(data.data);
            }

            // Fetch Kitchen Alerts directed to waiters
            const resAlerts = await fetch(`${BASE}/apimenu/v2/v2_alerts.php?action=fetch&target_role=waiter`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const dataAlerts = await resAlerts.json();
            if (dataAlerts.success && dataAlerts.alerts) {
                setKitchenAlerts(dataAlerts.alerts);
            }
        } catch (e) {
            console.error('Fetch service requests error:', e);
        } finally {
            setLoading(false);
        }
    }, [user?.id, token]);

    useEffect(() => {
        fetchRequests();
        const interval = setInterval(fetchRequests, 10000);
        return () => clearInterval(interval);
    }, [fetchRequests]);

    const resolveRequest = async (requestId) => {
        try {
            const res = await fetch(`${BASE}/apimenu/resolve_service_request.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ request_id: requestId, customer_id: user.id })
            });
            const data = await res.json();
            if (data.status === 'success') {
                setRequests(prev => prev.filter(r => r.id !== requestId));
            }
        } catch (e) {
            console.error('Resolve request error:', e);
        }
    };

    const dismissKitchenAlert = async (alertId) => {
        try {
            await fetch(`${BASE}/apimenu/v2/v2_alerts.php?action=resolve`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ alert_id: alertId })
            });
            setKitchenAlerts(prev => prev.filter(a => a.id !== alertId));
        } catch (e) {
            console.error('Dismiss kitchen alert error:', e);
        }
    };

    const allItems = [
        ...requests.map(r => ({ ...r, _type: 'customer' })),
        ...kitchenAlerts.map(a => ({ ...a, _type: 'kitchen' }))
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return (
        <div style={{ padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
            <Link to="/manage_v2_secure_auth/" className="sticky-back-btn">
                <i className="fas fa-arrow-left"></i> Back to Dashboard
            </Link>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '1.6rem', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <i className="fas fa-bell" style={{ color: '#eab308' }}></i> Service Requests
                    </h2>
                    <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '0.9rem' }}>
                        Monitor and resolve table requests in real-time.
                    </p>
                </div>
                {allItems.length > 0 && (
                    <div style={{ background: '#ef4444', color: '#fff', padding: '6px 16px', borderRadius: '50px', fontWeight: '700', fontSize: '0.9rem' }}>
                        {allItems.length} Pending
                    </div>
                )}
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>
                    <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem' }}></i>
                    <p>Loading requests...</p>
                </div>
            ) : allItems.length === 0 ? (
                <div style={{ background: '#fff', border: '1px dashed #d1d5db', borderRadius: '16px', padding: '60px 20px', textAlign: 'center' }}>
                    <i className="fas fa-check-circle" style={{ fontSize: '3rem', color: '#10b981', marginBottom: '15px' }}></i>
                    <h3 style={{ margin: 0, color: '#374151' }}>All caught up!</h3>
                    <p style={{ color: '#6b7280', fontSize: '0.9rem', marginTop: '5px' }}>No active service requests right now.</p>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                    {allItems.map(item => {
                        if (item._type === 'kitchen') {
                            return (
                                <div key={`kitchen-${item.id}`} style={{ background: '#fff', borderRadius: '16px', borderLeft: '5px solid #f97316', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '20px', transition: 'transform 0.2s' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                                        <div>
                                            <div style={{ color: '#f97316', fontWeight: '700', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Kitchen Alert</div>
                                            <h3 style={{ margin: '5px 0 0', fontSize: '1.2rem', color: '#1f2937', lineHeight: '1.4' }}>{item.message_text}</h3>
                                        </div>
                                    </div>
                                    
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6b7280', fontSize: '0.85rem', marginBottom: '20px' }}>
                                        <i className="far fa-clock"></i>
                                        {new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </div>

                                    <button 
                                        onClick={() => dismissKitchenAlert(item.id)}
                                        style={{ width: '100%', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', padding: '12px', borderRadius: '8px', fontWeight: '600', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'background 0.2s' }}
                                        onMouseOver={e => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#334155'; }}
                                        onMouseOut={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#475569'; }}
                                    >
                                        <i className="fas fa-times"></i> Dismiss
                                    </button>
                                </div>
                            );
                        }

                        return (
                            <div key={`customer-${item.id}`} style={{ background: '#fff', borderRadius: '16px', borderLeft: '5px solid #ef4444', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '20px', transition: 'transform 0.2s' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                                    <div>
                                        <div style={{ color: '#ef4444', fontWeight: '700', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Table Request</div>
                                        <h3 style={{ margin: '5px 0 0', fontSize: '1.4rem', color: '#1f2937' }}>Table {item.table_number}</h3>
                                    </div>
                                    <div style={{ background: '#fffbeb', color: '#b45309', padding: '6px 12px', borderRadius: '8px', fontWeight: '800', fontSize: '0.9rem', border: '1px solid #fef3c7' }}>
                                        {item.request_type}
                                    </div>
                                </div>
                                
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6b7280', fontSize: '0.85rem', marginBottom: '20px' }}>
                                    <i className="far fa-clock"></i>
                                    {new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>

                                <button 
                                    onClick={() => resolveRequest(item.id)}
                                    style={{ width: '100%', background: '#10b981', color: '#fff', border: 'none', padding: '12px', borderRadius: '8px', fontWeight: '600', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'background 0.2s' }}
                                    onMouseOver={e => e.currentTarget.style.background = '#059669'}
                                    onMouseOut={e => e.currentTarget.style.background = '#10b981'}
                                >
                                    <i className="fas fa-check-double"></i> Mark as Resolved
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ManageServiceRequests;
